import { tablesQueryKeys } from '@features/tables/api/queryKeys'
import { sidebarQueryKeys } from '@features/sidebar/api/queryKeys'
import { permissionsQueryKeys } from '@features/permissions/api/queryKeys'
import { settingsQueryKeys } from '@features/settings/api/queryKeys'
import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getConfiguration } from '../api/configuration.api'
import { getSettings } from '../api/settings.api'
import { getUserPreferences } from '../api/userPreferences.api'
import { getTables } from '../../tables/api/tables.api'
import { tableCapabilitiesQuery } from '../../tables/hooks/useCanCreateTables'
import { favoritesQuery } from '../../sidebar/hooks/useFavorites'
import { getTableOrganization } from '../../sidebar/api/tableOrganization.api'
import { getModules } from '../../permissions/api/permissions.api'
import type { UserPreferences } from '../models'
import { usePreferenceEditingStore } from '../stores/usePreferenceEditingStore'
import { hydrateResolvedConfiguration } from '../sync/settingsResolutionSync'
import { env } from '../../../config/env'
import { APP_NAME, NAVIGATION_QUERY_STALE_TIME_MS } from '../../../constants'
import Loading from '../../shared/components/Loading'
import { ErrorFallback } from '../../shared/components/ErrorBoundary'
import { ReconnectingScreen } from '../../shared/components/ReconnectingScreen'
import { isServerUnavailableError, isUpdatingError } from '../../shared/api/retryPolicy'

const hasClerkProvider = Boolean(env.VITE_CLERK_PUBLISHABLE_KEY)

export default function ConfigurationController({
  children,
  loadingFallback = <Loading />,
}: {
  children?: ReactNode
  loadingFallback?: ReactNode
}) {
  const { isLoaded, isSignedIn } = useAuth()
  const enabled = hasClerkProvider ? isLoaded && Boolean(isSignedIn) : true
  const hydrateUserPreferences = usePreferenceEditingStore((state) => state.hydrateUserPreferences)
  const queryClient = useQueryClient()
  const [hydrated, setHydrated] = useState(false)
  const { data, error, failureReason, refetch, isFetching } = useQuery({
    queryKey: settingsQueryKeys.configuration,
    queryFn: ({ signal }) => getConfiguration(signal),
    enabled,
  })

  const modulesQuery = useQuery({
    queryKey: permissionsQueryKeys.permissionsModules,
    queryFn: getModules,
    enabled,
    staleTime: NAVIGATION_QUERY_STALE_TIME_MS,
  })
  // Settings pages are only readable with the Settings module; skip the request
  // (and its 403) for everyone else.
  const canViewSettings = modulesQuery.data?.some((module) => module.key === 'settings') ?? false
  useQuery({
    queryKey: settingsQueryKeys.settings,
    queryFn: getSettings,
    enabled: enabled && canViewSettings,
    staleTime: NAVIGATION_QUERY_STALE_TIME_MS,
  })
  useQuery({
    queryKey: tablesQueryKeys.tables,
    queryFn: getTables,
    enabled,
    staleTime: NAVIGATION_QUERY_STALE_TIME_MS,
  })
  useQuery({
    queryKey: sidebarQueryKeys.tableOrganization,
    queryFn: getTableOrganization,
    enabled,
    staleTime: NAVIGATION_QUERY_STALE_TIME_MS,
  })
  useQuery({ ...tableCapabilitiesQuery, enabled })
  useQuery({ ...favoritesQuery, enabled })

  useLayoutEffect(() => {
    if (data) {
      hydrateResolvedConfiguration(data)
      setHydrated(true)
    }
  }, [data])

  useEffect(() => {
    if (data) {
      queryClient
        .prefetchQuery({
          queryKey: settingsQueryKeys.userPreferences,
          queryFn: getUserPreferences,
          staleTime: NAVIGATION_QUERY_STALE_TIME_MS,
        })
        .then(() => {
          const userPreferences = queryClient.getQueryData<UserPreferences>(
            settingsQueryKeys.userPreferences
          )
          if (userPreferences) hydrateUserPreferences(userPreferences)
        })
    }
  }, [data, hydrateUserPreferences, queryClient])

  useEffect(() => {
    if (error && import.meta.env.DEV) {
      console.error('Failed to hydrate resolved configuration', error)
    }
  }, [error])

  // Cached configuration can be stale even for the correct identity. Wait for
  // this session's authoritative response before mounting any themed app UI.
  // Once mounted, background refetches must not tear down the app.
  if (enabled && !hydrated) {
    // A deploy holding the app isn't an error: AppUpdateController covers the
    // page with the updating screen and reloads it when the update is over.
    if (isUpdatingError(error ?? failureReason)) return loadingFallback
    if (error && !isFetching) {
      return (
        <ErrorFallback
          message={
            isServerUnavailableError(error)
              ? `${APP_NAME} isn't responding right now. Try again in a minute.`
              : "Your preferences couldn't be loaded. Try again."
          }
          onReset={() => void refetch()}
        />
      )
    }
    // A server that isn't answering (a deploy restarting it) is retried for a
    // while (retryPolicy.ts); say so instead of spinning silently.
    if (isServerUnavailableError(failureReason)) return <ReconnectingScreen />
    return loadingFallback
  }

  return children
}
